import { fetchCall } from './fetchUtils';
import { ansiReset, buT, cT, pT, rT, yT } from './logStyling';
import {
	decrementTotalImagesUploaded,
	incrementUploadCount,
	setCurrentUploadDetails,
} from '@/slices/uploadSlice';
import { logToAi } from './logging';
import { setIsPrimaryKeyValid } from '@/slices/tempSlice';
import netreq from './netreq';

const valid = (obj) => obj !== null && obj !== undefined && obj !== '';

export const uploadImage =
	({
		file,
		userId,
		localDomain,
		enableFaceDetection,
		faceDetectionThreshold,
		isPrimaryKeyValidOverride = true,
	} = {}) =>
	(dispatch, getState) => {
		return new Promise((resolve, reject) => {
			if (file.size < 1) {
				console.error('You passed in a file with no data!');
				reject('You passed in a file with no data!');
				return;
			}
			const formData = new FormData();
			const retailer = getState()?.common?.retailerId;
			const isPrimaryKeyValid =
				getState()?.temp?.isPrimaryKeyValid &&
				isPrimaryKeyValidOverride;
			const fileName = encodeURIComponent(file.name);

			let fileType = file.type;
			if (!fileType && file?.name?.toLowerCase()?.endsWith('.heic')) {
				fileType = 'image/heic';
			}
			if (!fileType && file?.name?.toLowerCase()?.endsWith('.heif')) {
				fileType = 'image/heif';
			}
			if (!fileType && file?.name?.toLowerCase()?.endsWith('.pdf')) {
				fileType = 'application/pdf';
			}
			if (!fileType && file?.name?.toLowerCase()?.endsWith('.jpeg')) {
				fileType = 'image/jpeg';
			}
			if (!fileType && file?.name?.toLowerCase()?.endsWith('.jpg')) {
				fileType = 'image/jpg';
			}
			if (!fileType && file?.name?.toLowerCase()?.endsWith('.png')) {
				fileType = 'image/png';
			}
			if (!fileType && file?.name?.toLowerCase()?.endsWith('.bmp')) {
				fileType = 'image/bmp';
			}

			logToAi(
				{
					message: `Beginning image upload '${file?.name}'`,
					id: 'UPLOAD_IMAGE',
					location: 'uploadUtils.js:uploadImage',
					parameters: {
						file: {
							name: file?.name,
							size: file?.size,
							type: fileType,
						},
						userId: userId,
					},
				},
				'debug'
			);

			formData.append('file', file);

			let url =
				`/api/upload?userId=${userId}` +
				`&fileType=${fileType}` +
				`&fileName=${fileName}` +
				`&retailerId=${retailer}` +
				`&isPrimaryKeyValid=${isPrimaryKeyValid}` +
				`&faceDetection=${enableFaceDetection}`;

			if (typeof faceDetectionThreshold === 'number') {
				url += `&fdConfidenceThreshold=${faceDetectionThreshold}`;
			}
			let body = formData;

			if (localDomain) {
				const b64name = window?.btoa(
					String.fromCodePoint(...new TextEncoder().encode(file.name))
				);
				url = `${localDomain}/spelite/upload?name=${b64name}&size=${file?.size}&type=${fileType}&lastmodified=${file?.lastModified}`;
				body = file;
			}

			const metadata = {
				originalFileName: encodeURI(fileName),
			};
			fetchCall(
				url,
				'POST',
				body
				// {
				// 	'content-type': file.type,
				// }
			)
				.then((res) => {
					dispatch(incrementUploadCount());
					logToAi(
						{
							message: `Successfully uploaded image ${
								!isPrimaryKeyValidOverride
									? 'after retrying using secondary key '
									: ''
							}${file?.name}'`,
							id: 'UPLOAD_IMAGE_SUCCESS',
							location: 'uploadUtils.js:uploadImage',
							parameters: {
								file: {
									name: file?.name,
									size: file?.size,
									type: file?.type,
								},
								userId: userId,
							},
							data: {
								response: res,
							},
						},
						'debug'
					);
					resolve(res);
				})
				.catch((err) => {
					if (err?.statusCode === 401) {
						if (err?.usedPrimaryKey) {
							logToAi(
								{
									message: `Primary Key was invalid while uploading image '${file?.name}'`,
									id: 'UPLOAD_PRIMARY_KEY_ERROR',
									location: 'uploadUtils.js:uploadImage',
									parameters: {
										file: {
											name: file?.name,
											size: file?.size,
											type: file?.type,
										},
										userId: userId,
									},
									data: {
										error: err,
									},
								},
								'debug'
							);
							dispatch(setIsPrimaryKeyValid(false));
							dispatch(
								uploadImage({
									file: file,
									userId: userId,
									localDomain: localDomain,
									enableFaceDetection: enableFaceDetection,
									faceDetectionThreshold:
										faceDetectionThreshold,
									isPrimaryKeyValid: false,
								})
							)
								.then((res) => resolve(res))
								.catch((err) => reject(err));
						} else {
							logToAi(
								{
									message: `Secondary Key was invalid while uploading image '${file?.name}'`,
									id: 'UPLOAD_IMAGE_ERROR',
									location: 'uploadUtils.js:uploadImage',
									parameters: {
										file: {
											name: file?.name,
											size: file?.size,
											type: file?.type,
										},
										userId: userId,
									},
									data: {
										error: err,
									},
								},
								'error'
							);
							reject(err);
						}
					} else {
						logToAi(
							{
								message: `Error while uploading image '${file?.name}'`,
								id: 'UPLOAD_IMAGE_ERROR',
								location: 'uploadUtils.js:uploadImage',
								parameters: {
									file: {
										name: file?.name,
										size: file?.size,
										type: file?.type,
									},
									userId: userId,
								},
								data: {
									error: err,
								},
							},
							'error'
						);
						reject(err);
					}
				});
		});
	};

/**
 * Generates a random 23 number string seeded with the current date and time.
 */
export const getGuid = () => {
	let newId = new Date().getTime().toString();
	while (newId.length < 23) {
		newId += Math.floor(Math.random() * 10);
	}
	return newId;
};

/**
 * Converts a `Blob` into a `File`
 * @param {Blob} imageBlob The blob to be converted to a file
 * @param {{id: string, name: string, image: string, mimeType: string}} imageDetails
 * Details about the blob being converted
 * @returns {File} A `File` with a `name` and `type` from the imageDetails
 * and binary data from the `imageBlob`
 */
export const getFileFromImageBlob = (imageBlob, imageDetails) => {
	if (imageBlob && imageBlob?.size) {
		const typeFromBlob = imageBlob?.type?.split('/')[1].toLocaleLowerCase();
		const extFromGooglePhotos = imageDetails?.name
			?.match(/(\.[\w\d]+)(?=[\?]|$)/)?.[1]
			.toLocaleLowerCase();
		const typeFromGooglePhotos = imageDetails?.mimeType
			?.split('/')[1]
			.toLocaleLowerCase();

		console.debug(
			`${pT}Type from blob ${buT}${typeFromBlob}${pT}` +
				` vs Extension from Google Photos image details ${buT}${extFromGooglePhotos}${pT}` +
				` vs Type from from Google Photos image details ${buT}${typeFromGooglePhotos}${ansiReset}.` +
				` ${rT}RULE OF THUMB${yT}: Always trust the MIME Type.${ansiReset}`
		);
		const ext =
			extFromGooglePhotos || typeFromBlob || typeFromGooglePhotos || '';
		return new File(
			[imageBlob],
			`${imageDetails?.name.split('.')[0]}-${Date.now()}${ext}`,
			{
				type: imageDetails?.mimeType || imageBlob?.type || 'image/*',
			}
		);
	} else {
		throw "The blob wasn't found or had no size";
	}
};

/**
 * Accepts a `Blob` or `File` and uses a temporary `<a>` anchor element that is
 * clicked via a JavaScript click `MouseEvent` to download the file.
 * @param {Blob | File} data The Blob or File to be downloaded
 * @param {string} name The name of the file **INCLUDING** the file extension
 */
export function downloadBlobOrFile(data, name = 'file.txt') {
	console.debug(`${cT}downloadBlobOrFile${pT}: Downloading ${name}`, data);

	// Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
	const dataUrl = URL.createObjectURL(data);

	// Create a link element
	const link = document.createElement('a');

	// Set link's href to point to the Blob URL
	link.href = dataUrl;
	link.download = name;

	// Append link to the body
	document.body.appendChild(link);

	// Dispatch click event on the link
	// This is necessary as link.click() does not work on the latest firefox
	link.dispatchEvent(
		new MouseEvent('click', {
			bubbles: true,
			cancelable: true,
			view: window,
		})
	);

	// Remove link from body
	document.body.removeChild(link);
}

export const getUploadDetails = (userId) => (dispatch, getState) => {
	return new Promise((resolve, reject) => {
		try {
			const retailer = getState()?.common?.retailerId;
			if (!valid(userId)) {
				throw new Error('userId is required to get upload details');
			}
			if (!valid(retailer)) {
				throw new Error('retailerId is required to get upload details');
			}
			return fetchCall(
				`/api/csx-storage/v1/user-upload-details?retailerId=${retailer}`,
				'GET',
				null,
				{
					'kmmp-user-id': userId,
				}
			)
				.then((res) => {
					dispatch(setCurrentUploadDetails(res));
					resolve(res);
				})
				.catch((err) => {
					logToAi(
						{
							message: `Error while fetching upload details for user '${userId}'`,
							id: 'GET_UPLOAD_DETAILS_ERROR',
							location: 'uploadUtils.js:getUploadDetails',
							parameters: {
								userId: userId,
							},
							data: {
								error: err,
							},
						},
						'error'
					);
					reject(err);
				});
		} catch (err) {
			logToAi(
				{
					message: `Error while fetching upload details for user '${userId}'`,
					id: 'GET_UPLOAD_DETAILS_ERROR',
					location: 'uploadUtils.js:getUploadDetails:tryCatchBlock',
					parameters: {
						userId: userId,
					},
					data: {
						error: err,
					},
				},
				'error'
			);
			reject(err);
		}
	});
};

/**
 * Updates the upload details object for the given user.
 *
 * This is how we pass information about the upload status from this
 * moments-transfer app to whatever app opened it like the next-gen-kiosk app.
 * @param {string} userId
 * @param {{totalCount: number, failedCount: number}} details
 * @returns {Promise<any>}
 */
export const updateUploadDetails =
	(userId, details) => (dispatch, getState) => {
		return new Promise((resolve, reject) => {
			try {
				const retailer = getState()?.common?.retailerId;
				if (!valid(userId)) {
					throw new Error(
						'userId is required to update upload details'
					);
				}
				if (!valid(details)) {
					throw new Error(
						'details are required to update upload details'
					);
				}

				const body = {
					...details,
				};
				return fetchCall(
					`/api/csx-storage/v1/user-upload-details?retailerId=${retailer}`,
					'PUT',
					body,
					{
						'kmmp-user-id': userId,
					}
				)
					.then((res) => {
						resolve(res);
					})
					.catch((err) => {
						logToAi(
							{
								message: `Error while updating upload details for user '${userId}'`,
								id: 'UPDATE_UPLOAD_DETAILS_ERROR',
								location: 'uploadUtils.js:updateUploadDetails',
								parameters: {
									userId: userId,
									details: details,
								},
								data: {
									error: err,
								},
							},
							'error'
						);
						reject(err);
					});
			} catch (err) {
				logToAi(
					{
						message: `Error while updating upload details for user '${userId}'`,
						id: 'UPDATE_UPLOAD_DETAILS_ERROR',
						location:
							'uploadUtils.js:updateUploadDetails:tryCatchBlock',
						parameters: {
							userId: userId,
							details: details,
						},
						data: {
							error: err,
						},
					},
					'error'
				);
				reject(err);
			}
		});
	};
